<template>
  <div class="kpi-details">
    <div class="kpi-details-header">
      <div class="kpi-details-info">
        <p class="kpi-details-name">{{ kpiData.label }}</p>
        <p class="kpi-details-description">{{ kpiData.group }}</p>
      </div>
    </div>

    <div class="kpi-details-filters">
      <BDropdown size="sm"
                  variant="outline-primary"
                  menu-class="w-100"
                  :disabled="!countryOptions || countryOptions.length === 0">
        <template #button-content>
          <div class="d-flex align-items-center justify-content-center w-100">
            <Globe :size="12" class="me-2" />
            <span>{{ selectedCountryLabel }}</span>
          </div>
        </template>
        <BDropdownHeader id="dropdown-header-1">Land auswählen</BDropdownHeader>
        <BDropdownItemButton v-for="country in countryOptions"
                                :key="country.id"
                                aria-describedby="dropdown-header-1"
                                @click="kpiData.selectedCountry = country.id">
          <Globe :size="12" class="me-2" /> {{ country.label }}
        </BDropdownItemButton>
      </BDropdown>

      <BDropdown size="sm"
                  variant="outline-primary"
                  class="w-100"
                  menu-class="w-100"
                  :disabled="!waveOptions || waveOptions.length === 0">
        <template #button-content>
          <div class="d-flex align-items-center justify-content-center w-100">
            <Activity :size="12" class="me-2" />
            <span>{{ nicifyWaveLabel(selectedWaveLabel) }}</span>
          </div>
        </template>
        <BDropdownHeader id="dropdown-header-2">Welle auswählen</BDropdownHeader>
        <BDropdownItemButton v-for="wave in waveOptions"
                                :key="wave.id"
                                aria-describedby="dropdown-header-2"
                                @click="kpiData.selectedWave = wave.id">
          <Activity :size="12" class="me-2" /> {{ nicifyWaveLabel(wave.label) }}
        </BDropdownItemButton>
      </BDropdown>
    </div>

    <BButton variant="primary"
              size="sm"
              class="add-button"
              :disabled="!canAddKpi"
              @click="$emit('add', kpiData)">
      <Plus :size="12" class="me-1" />
      Hinzufügen
    </BButton>
  </div>
</template>

<script>
import { Globe, Activity, Plus } from 'lucide-vue-next';

export default {
  name: 'KpiDetails',

  components: {
    Globe,
    Activity,
    Plus
  },

  props: {
    kpi: {
      type: Object,
      required: true
    },
    countryOptions: {
      type: Array,
      default: () => []
    },
    waveOptions: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      kpiData: this.kpi
    };
  },

  mounted() {
    if (this.kpiData.selectedCountry === null)
      this.kpiData.selectedCountry = this.countryOptions.length > 0 ? this.countryOptions[0].id : null;
    if (this.kpiData.selectedWave === null)
      this.kpiData.selectedWave = this.waveOptions.length > 0 ? this.waveOptions[0].id : null;
  },
  emits: ['add'],

  computed: {
    selectedCountryLabel() {
      const country = this.countryOptions.find(c => c.id === this.kpiData.selectedCountry);
      return country ? country.label : 'Land auswählen';
    },

    selectedWaveLabel() {
      const wave = this.waveOptions.find(w => w.id === this.kpiData.selectedWave);
      return wave ? wave.label : 'Welle auswählen';
    },

    canAddKpi() {
      return this.kpiData.selectedCountry && this.kpiData.selectedWave; // && this.kpiData.selectedSubKpi;
    }
  },

  methods: {
    nicifyWaveLabel(label) {
      return label.replace(/_/g, ' / ');
    }
  }
}
</script>

<style lang="scss" scoped>
.kpi-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  transition: all 0.15s;

  &:hover {
    border-color: rgba(var(--bmw-primary-rgb), 0.3);
  }
}

.kpi-details-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.kpi-details-info {
  flex: 1;
  min-width: 0;
}

.kpi-details-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0 0 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-details-description {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0;
}

.kpi-details-filters {
  display: flex;
  flex-direction: column;
  gap: 8px;

  :deep(.dropdown) {
    width: 100%;
  }

  :deep(.dropdown-menu) {
    width: 100%;
  }

  :deep(.btn) {
    width: 100%;
    text-align: center;
    font-size: 12px;
    height: 28px;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.dropdown-toggle::after) {
    margin-left: auto;
  }
}

.filter-select {
  font-size: 12px;
  height: 28px;

  :deep(.form-select) {
    font-size: 12px;
    height: 28px;
    line-height: 28px;
    padding-top: 0;
    padding-bottom: 0;
  }
}

.select-icon {
  margin-right: 4px;
  vertical-align: middle;
  display: inline-block;
}

.add-button {
  width: 100%;
  height: 28px;
  font-size: 12px;
  background: var(--bmw-primary);
  border-color: var(--bmw-primary);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #012a52;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Dark Mode
:root.dark,
[data-bs-theme="dark"] {
  .kpi-details {
    background: var(--color-card);
    border-color: var(--color-border);
  }

  .kpi-details-name {
    color: var(--color-foreground);
  }

  .kpi-details-description {
    color: var(--color-muted-foreground);
  }
}
</style>
